import { all, takeLatest, put, call, select } from "redux-saga/effects";
import { toast } from 'react-hot-toast';
import accountSlice, { loadProfile, selectProfile, updateProfile } from "./slice";
import { IConfiguration } from "../../shared/types";
import { SimplyTestApi } from "../../api/simplytest";
import { OrderGridParams } from "../../orders/types";
import { selectConfig } from "../../shared/redux/slice";
import { selectIdToken, unauthorized } from "../../auth/redux/slice";
import { AxiosResponse } from "axios";
import { AccountProfile } from "../types";

export abstract class AccountSagas {
    public static* run() {
        try {
            yield all([
                takeLatest(loadProfile, AccountSagas.loadProfile),
                takeLatest(updateProfile, AccountSagas.updateProfile),
            ]);
        }
        catch(e: any){
            toast.error('Unable to register Account module.');
            console.log(`[AccountSagas:run] the following exception has occurred`, e);
        }
    }

    public static* loadProfile(action: ReturnType<typeof loadProfile>) {
        try {
            yield put(accountSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const profileResponse: AxiosResponse<AccountProfile> = yield call(
                {
                    context: client,
                    fn: client.getAccountProfile
                }
            );

            if(profileResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(profileResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(accountSlice.actions.setProfile(profileResponse.data));
        }
        catch(e: any){
            toast.error('Unable to load profile.');
            console.log(`[AccountSagas:loadProfile] the following exception has occurred`, e);
        }
        finally {
            yield put(accountSlice.actions.setState('READY'));
        }
    }

    public static* updateProfile(action: ReturnType<typeof updateProfile>) {
        const { payload: { profile }} = action;
        try {
            yield put(accountSlice.actions.setState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            const currentProfile: AccountProfile | undefined = yield select(selectProfile);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const updateProfileResponse: AxiosResponse<AccountProfile> = yield call(
                {
                    context: client,
                    fn: client.updateAccountProfile
                },
                {
                    ...profile
                }
            );

            if(updateProfileResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(updateProfileResponse.status !== 200)
                throw new Error("Error communicating with Api");

            // The update endpoint never returns roleName — restore it from the current profile.
            yield put(accountSlice.actions.setProfile({ ...updateProfileResponse.data, roleName: currentProfile?.roleName }));
            yield call({ context: toast, fn: toast.success}, `Your account has been updated.`);
        }
        catch(e: any){
            toast.error('Unable to update profile.');
            console.log(`[AccountSagas:updateProfile] the following exception has occurred`, e);
        }
        finally {
            yield put(accountSlice.actions.setState('READY'));
        }
    }
}